import { isBackgroundNotSelected, isStoreImageBackground } from '@/components/Modals/BackgroundGallery/utils';
import { toast } from '@/components/toast';
import { createImagesDns } from '@/environment';
import { DidUser } from '@/services/auth';
import { logger } from '@/services/logger';
import { rootStore } from '@/state/root/store';
import { formatS3Url } from '@/utils/s3';
import { groupBy } from '@/utils/utils';
import { Presenter, VideoType } from '@d-id/types';
import { nanoid } from 'nanoid';
import { displayInPlayground, groupByLongestPrefix } from '../../../../utils';

export const onApplyBackgroundSuccess = async ({
    resultUrl,
    videoType,
    backgroundUrl,
    backgroundColor,
}: {
    resultUrl: string;
    videoType?: VideoType;
    backgroundUrl?: string;
    backgroundColor?: string;
}) => {
    const formatResultUrl = formatS3Url(createImagesDns, resultUrl);

    if (formatResultUrl) {
        if (videoType === VideoType.CLIP) {
            updatePresenterBackground(formatResultUrl, backgroundUrl, backgroundColor);
        } else {
            updatePresenterBackground(formatResultUrl);
        }

        displayInPlayground(formatResultUrl);

        // await to make sure the image is loaded
        await new Promise(resolve => setTimeout(resolve, 100));
    }
};

const startBackgroundProcessing = (presenterId: string, showAgentLoader: () => void): void => {
    rootStore.agentEditorStore.setBackgroundProcessingPresenters(prev => prev.add(presenterId));
    showAgentLoader();
};

const getOwnerId = (user: DidUser): string | undefined => {
    return user?.org_id ? `${user.org_id}/${user.sub}` : user?.sub;
};

const doesImageExist = async (url: string): Promise<boolean> => {
    try {
        const response = await fetch(url, { method: 'HEAD' });
        return response.ok;
    } catch {
        return false;
    }
};

const getDefaultPresenterTransparentUrl = (presenterPoster: string, createImagesDns: string): string | undefined => {
    if (!presenterPoster.includes('/DefaultPresenters/')) {
        return;
    }

    const presenterName = presenterPoster.split('/DefaultPresenters/')[1]?.split('/')?.[0];
    if (!presenterName) {
        return;
    }

    return `${createImagesDns}/DefaultPresenters/${presenterName}/v2_transparent_image.png`;
};

const getExistingDefaultTransparentUrl = async (presenterPoster: string, createImagesDns: string): Promise<string | undefined> => {
    const defaultTransparentUrl = getDefaultPresenterTransparentUrl(presenterPoster, createImagesDns);
    if (!defaultTransparentUrl) {
        return;
    }

    if (await doesImageExist(defaultTransparentUrl)) {
        return defaultTransparentUrl;
    }
    return;
};

const getRemoveBgUrl = async (createImagesDns: string, ownerId: string, presenterId: string): Promise<string | undefined> => {
    const removedBackgroundUrl = `${createImagesDns}/${ownerId}/${presenterId}/image-removed_bg.png`;
    if (await doesImageExist(removedBackgroundUrl)) {
        return removedBackgroundUrl;
    }
    return;
};

const processBackgroundRemoval = async ({
    presenterId,
    user,
    presenterPoster,
    createImagesDns,
    createImagesBucket,
    removeBackground,
}: {
    presenterId: string;
    user: DidUser;
    presenterPoster: string;
    createImagesDns: string;
    createImagesBucket: string;
    removeBackground: (url: string, id: string, bucket: string) => Promise<string | undefined>;
}): Promise<string> => {
    if (!user) {
        throw new Error('User not found');
    }

    const presenterTransparentUrl = await getExistingDefaultTransparentUrl(presenterPoster, createImagesDns);
    if (presenterTransparentUrl) {
        return presenterTransparentUrl;
    }

    const ownerId = getOwnerId(user);
    if (!ownerId) {
        throw new Error('Owner id not found');
    }

    const presenterRemovedBgUrl = await getRemoveBgUrl(createImagesDns, ownerId, presenterId);
    if (presenterRemovedBgUrl) {
        return presenterRemovedBgUrl;
    }

    const removedBgUrl = await removeBackground(presenterPoster, presenterId, createImagesBucket);
    if (!removedBgUrl) {
        throw new Error('Failed to remove background');
    }

    if (!removedBgUrl.includes(createImagesBucket)) {
        throw new Error('Invalid removed background URL, does not contain create-images bucket name');
    }

    return formatS3Url(createImagesDns, removedBgUrl);
};

const updatePresenterBackground = (backgroundAppliedUrl: string, backgroundSourceUrl?: string, backgroundColor?: string): void => {
    const thumbnailUrl = backgroundAppliedUrl.replace(/-image\..+$/, '-thumbnail.jpeg');

    if (backgroundColor) {
        rootStore.agentEditorStore.setPresenterBackgroundColor(backgroundColor);
        rootStore.agentEditorStore.setPresenterBackgroundSourceUrl('');
        rootStore.agentEditorStore.setPresenterBackgroundThumbnailUrl(thumbnailUrl);
        return;
    }

    if (backgroundSourceUrl) {
        rootStore.agentEditorStore.setPresenterBackgroundSourceUrl(backgroundSourceUrl);
        rootStore.agentEditorStore.setPresenterBackgroundColor('');
        rootStore.agentEditorStore.setPresenterBackgroundThumbnailUrl(thumbnailUrl);
        return;
    }

    rootStore.agentEditorStore.setPresenterBackgroundSourceUrl(backgroundAppliedUrl);
    rootStore.agentEditorStore.setPresenterBackgroundColor('');
    rootStore.agentEditorStore.setPresenterBackgroundThumbnailUrl(thumbnailUrl);
};

const cleanupBackgroundProcessing = (presenterId: string | undefined, hideAgentLoader: () => void): void => {
    if (presenterId) {
        rootStore.agentEditorStore.setBackgroundProcessingPresenters(
            (prev: Set<string>) => new Set([...prev].filter(id => id !== presenterId))
        );
    }
    hideAgentLoader();
};

interface BackgroundUpdateOptions {
    presenter?: Presenter;
    user?: DidUser | null;
    background: string;
    createImagesDns?: string;
    createImagesBucket?: string;
    removeBackground: (url: string, id: string, bucket: string) => Promise<string | undefined>;
    applyBackground: (params: any) => Promise<{ result_url: string }>;
    applyBackgroundColor: (params: any) => Promise<string>;
    showAgentLoader: () => void;
    hideAgentLoader: () => void;
}

export const onUpdateBackground = async ({
    presenter,
    user,
    background,
    createImagesDns,
    createImagesBucket,
    removeBackground,
    applyBackground,
    applyBackgroundColor,
    showAgentLoader,
    hideAgentLoader,
}: BackgroundUpdateOptions) => {
    const presenterPoster = presenter?.poster;
    const presenterId = presenter?.id;

    if (
        !presenter ||
        !presenterId ||
        !background ||
        isBackgroundNotSelected(background) ||
        !presenterPoster ||
        !user ||
        !createImagesDns ||
        !createImagesBucket
    ) {
        return;
    }

    try {
        startBackgroundProcessing(presenterId, showAgentLoader);
        const removedBgResultUrl = presenter.is_greenscreen
            ? presenter.thumbnail
            : await processBackgroundRemoval({
                  presenterId,
                  user,
                  presenterPoster,
                  createImagesDns,
                  createImagesBucket,
                  removeBackground,
              });

        if (!removedBgResultUrl) {
            throw new Error('No removed bg result url');
        }
        isStoreImageBackground(background)
            ? await applyBackground({
                  source_url: removedBgResultUrl,
                  background_url: background,
                  bucket: createImagesBucket,
                  image_id: presenterId,
              })
            : await applyBackgroundColor({
                  imageUrl: removedBgResultUrl,
                  imageId: presenterId,
                  backgroundColor: background,
                  fileName: `merged-bg-color-${nanoid(8)}-image.png`,
                  bucket: createImagesBucket,
                  dns: createImagesDns,
              });
    } catch (error) {
        logger.error('Error applying background', { user, presenterId, error });
        toast.error({ title: 'Error applying background' });
    } finally {
        cleanupBackgroundProcessing(presenterId, hideAgentLoader);
    }
};

export const prepareBgPresentersList = (presenters: Presenter[], migratableAvatars: string[]): Presenter[] => {
    const { privateAgents = [], publicAgents = [] } = groupBy(presenters, p =>
        !p.id.includes('v2_public') ? 'privateAgents' : 'publicAgents'
    );

    const withOnlyMigratedPluses = publicAgents.filter(p => p.type !== VideoType.CLIP || migratableAvatars.some(a => p.id.includes(a)));
    const pluses = withOnlyMigratedPluses.filter(p => p.id.startsWith('v2_'));
    const plusesByPrefix = Object.values(groupByLongestPrefix(pluses, p => p.id));

    const excludeThisModels = plusesByPrefix
        .flatMap(presentersGroup => {
            const selectedPresenterId = presentersGroup.find(p => !(p.id.includes('green_screen') || p.id.includes('video_bg')))?.id;

            return presentersGroup.filter(p => p.id !== selectedPresenterId);
        })
        .map(p => p.id);

    return [...privateAgents, ...withOnlyMigratedPluses.filter(p => !excludeThisModels.some(excludedId => excludedId === p.id))];
};

export const groupAvatarsWithAlternatives = (presenters: Presenter[], grouping: Record<string, string[]>) => {
    const mainAvatars = Object.keys(grouping).flat();
    const onlyAlternatives = Object.values(grouping).flat();

    const { privateAgents = [], publicAgents = [] } = groupBy(presenters, p =>
        !p.id.includes('v2_public') ? 'privateAgents' : 'publicAgents'
    );

    return [
        ...privateAgents,
        ...publicAgents.filter(p => {
            // if the avatar is a main avatar, we want to show it
            if (mainAvatars.includes(p.id)) {
                return true;
            }

            // if the avatar is not main avatar, and is used as alternative, we want to hide it
            return !onlyAlternatives.some(a => p.id.includes(a));
        }),
    ];
};

export const filterV2PresenterLabel = (presenters: Presenter[]): Presenter[] => {
    return presenters.filter(p => !p?.labels?.includes('agents-v2') || p?.labels?.includes('agents-v1'));
};
